import { useNavigate, useSearch } from '@tanstack/react-router'
import { endOfDay, format, startOfDay, startOfMonth } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'

interface DateRangeResult {
  fromDate: Date | undefined
  toDate: Date | undefined
  setFromDate: (date: Date | undefined) => void
  setToDate: (date: Date | undefined) => void
  dateFrom: string | undefined
  dateTo: string | undefined
  clearDates: () => void
}

const parseDate = (dateStr: string | undefined): Date | undefined => {
  if (!dateStr) return undefined
  const [y, m, d] = dateStr.split('-').map(Number)
  if (y && m && d) return new Date(y, m - 1, d)
  return undefined
}

export function useDateRange(defaults?: { fromDate?: Date; toDate?: Date }): DateRangeResult {
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as Record<string, string>

  const [fromDate, setFromDateState] = useState<Date | undefined>(() => {
    const parsed = parseDate(search.dateFrom)
    return parsed ? startOfDay(parsed) : defaults?.fromDate ?? undefined
  })
  const [toDate, setToDateState] = useState<Date | undefined>(() => {
    const parsed = parseDate(search.dateTo)
    return parsed ? endOfDay(parsed) : defaults?.toDate ?? undefined
  })

  useEffect(() => {
    const parsedFrom = parseDate(search.dateFrom)
    if (parsedFrom) setFromDateState(startOfDay(parsedFrom))
    const parsedTo = parseDate(search.dateTo)
    if (parsedTo) setToDateState(endOfDay(parsedTo))
  }, [search.dateFrom, search.dateTo])

  const updateQueryParams = useCallback((newParams: Record<string, string | undefined>) => {
    navigate({
      search: (old: Record<string, string | undefined>) => ({ ...old, ...newParams }),
      replace: true,
    } as any)
  }, [navigate])

  const setFromDate = useCallback((date: Date | undefined) => {
    date && setFromDateState(startOfDay(date))
    updateQueryParams({ dateFrom: date ? format(date, 'yyyy-MM-dd') : undefined })
  }, [updateQueryParams])

  const setToDate = useCallback((date: Date | undefined) => {
    date && setToDateState(endOfDay(date))
    updateQueryParams({ dateTo: date ? format(date, 'yyyy-MM-dd') : undefined })
  }, [updateQueryParams])

  const clearDates = useCallback(() => {
    setFromDateState(undefined)
    setToDateState(undefined)
    updateQueryParams({ dateFrom: undefined, dateTo: undefined })
  }, [updateQueryParams])

  return {
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    dateFrom: fromDate ? format(fromDate, 'yyyy-MM-dd') : defaults?.fromDate ? format(defaults.fromDate, 'yyyy-MM-dd') : undefined,
    dateTo: toDate ? format(toDate, 'yyyy-MM-dd') : defaults?.toDate ? format(defaults.toDate, 'yyyy-MM-dd') : undefined,
    clearDates,
  }
}
